import {
  BusinessDomainEntity,
  BusinessUnitEntity,
  BusinessUnitList,
  CategoryEntity,
  ProductGroupEntity,
} from '@/domains/business-unit/entities';
import {
  BusinessUnitPayloadForm,
  QueryBusinessUnitPayload,
} from '@/domains/business-unit/ports/payloads';
import { BusinessUnitRepository } from '@/domains/business-unit/ports/repositories';
import { HttpClientAdapter } from '@/graphql/http-client-adapter';
import { productGraphqlUrl } from '@/graphql/config';
import {
  CREATE_BU,
  DEACTIVATE_BU,
  DELETE_BU,
  GET_ALL_BU,
  GET_BU_BY_ID,
  GET_BU_LIST,
  GET_BUSINESS_DOMAIN_BY_ID,
  GET_BUSINESS_DOMAIN_LIST,
  GET_CATEGORY_LIST,
  GET_PRODUCT_GROUPS,
  UPDATE_BU,
} from '../../graphql';

// Product catalog GraphQL endpoints on base-api are public reference data (no Firebase auth required).
const PUBLIC_CATALOG_QUERY_OPTS = { requiredAuth: false as const };

type ProductGroupFindAllResult = { data?: ProductGroupEntity[] } | null | undefined;

function toActiveProductGroups(result: ProductGroupFindAllResult): ProductGroupEntity[] {
  const groups = result?.data;
  if (!Array.isArray(groups)) {
    return [];
  }
  return groups.filter((group) => group.isActive !== false);
}

export class BusinessUnitHttpRepository implements BusinessUnitRepository {
  async getAllBU(): Promise<BusinessUnitEntity[]> {
    const response: BusinessUnitEntity[] = await HttpClientAdapter.query<BusinessUnitEntity[]>({
      query: GET_ALL_BU,
      dataKey: 'BusinessUnit_getAll',
    });
    return response;
  }

  async getById(id: string): Promise<BusinessUnitEntity> {
    const response = await HttpClientAdapter.query<BusinessUnitEntity>({
      query: GET_BU_BY_ID,
      dataKey: 'BusinessUnit_findById',
      variables: { id },
    });
    return response;
  }

  async getListBU(payload?: QueryBusinessUnitPayload): Promise<BusinessUnitList> {
    const response: BusinessUnitList = await HttpClientAdapter.query<BusinessUnitList>({
      query: GET_BU_LIST,
      dataKey: 'BusinessUnit_find',
      variables: { query: { sortBy: ['lastModifiedAt|desc', '_id|desc'], ...payload } },
    });
    return response;
  }

  async findCategory(): Promise<CategoryEntity[]> {
    const response = await HttpClientAdapter.query<CategoryEntity[] | null | undefined>({
      query: GET_CATEGORY_LIST,
      dataKey: 'Segments_findAll',
    });
    return Array.isArray(response) ? response : [];
  }

  async findProductGroups(): Promise<ProductGroupEntity[]> {
    const response = await HttpClientAdapter.query<ProductGroupFindAllResult>(
      {
        query: GET_PRODUCT_GROUPS,
        dataKey: 'ProductGroup_findAll',
      },
      { uri: productGraphqlUrl, ...PUBLIC_CATALOG_QUERY_OPTS },
    );
    return toActiveProductGroups(response);
  }

  async createBU(payload: BusinessUnitPayloadForm): Promise<BusinessUnitEntity> {
    const response = await HttpClientAdapter.mutate<BusinessUnitEntity>({
      mutation: CREATE_BU,
      dataKey: 'BusinessUnit_create',
      variables: { payload },
    });
    return response;
  }

  async updateBU(payload: BusinessUnitPayloadForm): Promise<BusinessUnitEntity> {
    const response = await HttpClientAdapter.mutate<BusinessUnitEntity>({
      mutation: UPDATE_BU,
      dataKey: 'BusinessUnit_update',
      variables: { payload },
    });
    return response;
  }

  async deactivateBU(id: string): Promise<BusinessUnitEntity> {
    const response = await HttpClientAdapter.mutate<BusinessUnitEntity>({
      mutation: DEACTIVATE_BU,
      dataKey: 'BusinessUnit_deactivate',
      variables: { id },
    });
    return response;
  }

  async deleteBU(id: string): Promise<boolean> {
    const response = await HttpClientAdapter.mutate<boolean>({
      mutation: DELETE_BU,
      dataKey: 'BusinessUnit_delete',
      variables: { id },
    });
    return response;
  }

  async getListBusinessDomain(payload?: QueryBusinessUnitPayload): Promise<BusinessDomainEntity[]> {
    const response: BusinessDomainEntity[] = await HttpClientAdapter.query<BusinessDomainEntity[]>({
      query: GET_BUSINESS_DOMAIN_LIST,
      dataKey: 'BusinessDomain_getList',
      variables: { query: payload },
    });
    return response;
  }

  async getBusinessDomainById(id: string): Promise<BusinessDomainEntity> {
    const response: BusinessDomainEntity = await HttpClientAdapter.query<BusinessDomainEntity>({
      query: GET_BUSINESS_DOMAIN_BY_ID,
      dataKey: 'BusinessDomain_getById',
      variables: { id },
    });
    return response;
  }
}
